import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, Briefcase, CheckCircle, ChevronDown, Loader2, MapPin, Pencil, Users } from 'lucide-react';
import { createJobConnectIdempotencyKey, jobConnectApi, turnoverItApi } from '../../services/api';

const callbackUrl = `${window.location.origin.replace(/^http:/, 'https:')}/api/v1/webhooks/jobconnect-applications`;

const initialDraft = need => ({
  reference: `offre-${need.id}`,
  contract: ['PERMANENT'],
  title: need.title || '',
  description: need.description || '',
  skills: [],
  renewable: false,
  pushToTop: false,
  location: { country: 'France', region: need.jdLocation || need.jdAnalysis?.location || '' },
  remote: 'PARTIAL',
  application: { callbackUrl },
  status: 'DRAFT',
});

const initialSkillQuery = need => (
  need.jdRequiredSkills?.[0] || need.jdAnalysis?.requiredskills?.[0] || ''
);

const contractLabels = {
  PERMANENT: 'CDI', TEMPORARY: 'CDD', FREELANCE: 'Freelance', INTERNSHIP: 'Stage', APPRENTICESHIP: 'Alternance',
};
const remoteLabels = { NONE: 'Sur site', PARTIAL: 'Télétravail partiel', FULL: 'Télétravail complet' };
const displayLocation = location => (
  [location?.locality, location?.region].filter(Boolean).join(', ') || location?.postalCode || 'Non renseigné'
);

const validate = draft => {
  const errors = {};
  const titleLength = draft.title.trim().length;
  const descriptionLength = draft.description.trim().length;
  const locationLength = (draft.location.region || draft.location.postalCode || '').trim().length;
  if (titleLength < 5 || titleLength > 100) errors.title = 'Le titre doit contenir entre 5 et 100 caractères.';
  if (descriptionLength < 500 || descriptionLength > 8000) errors.description = 'La description doit contenir entre 500 et 8000 caractères.';
  if (locationLength < 2 || locationLength > 150) errors.location = 'Indiquez un lieu public de 2 à 150 caractères.';
  if (!draft.skills.length) errors.skills = 'Ajoutez au moins une compétence.';
  if (!draft.contract.length) errors.contract = 'Choisissez un contrat.';
  return errors;
};

const fieldClass = 'mt-1 min-h-[44px] w-full rounded-md border-gray-300 text-sm focus:border-blue-600 focus:ring-blue-600';
const buttonClass = 'min-h-[44px] rounded-md px-4 py-2 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500';

const JobConnectDiffusionPanel = ({ need, canMutate, applicationCount = 0, onOpenApplications }) => {
  const [state, setState] = useState('loading');
  const [publication, setPublication] = useState(null);
  const [draft, setDraft] = useState(() => initialDraft(need));
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState('');
  const [sourceOpen, setSourceOpen] = useState(false);
  const [confirmApproval, setConfirmApproval] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [saving, setSaving] = useState(false);
  const [skillQuery, setSkillQuery] = useState('');
  const [skillSuggestions, setSkillSuggestions] = useState([]);
  const [skillSearchState, setSkillSearchState] = useState('idle');
  const [locationQuery, setLocationQuery] = useState(() => initialDraft(need).location.region);
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [locationSearchState, setLocationSearchState] = useState('idle');
  const selectedLocationQuery = useRef('');
  const creationAttempt = useRef(null);
  const approvalKey = useRef(null);
  const deletionKey = useRef(null);
  const synchronizationKey = useRef(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) {
      setState('loading');
      setMessage('');
    }
    try {
      const response = await jobConnectApi.listPublications(need.id);
      const current = response.items?.find(item => item.effectiveStatus !== 'DELETED') || null;
      setPublication(current);
      if (current?.draftPayload) {
        setDraft(current.draftPayload);
        const location = displayLocation(current.draftPayload.location);
        selectedLocationQuery.current = location;
        setLocationQuery(location);
      }
      setState(current ? 'ready' : 'empty');
    } catch (error) {
      if (!silent) setState('error');
    }
  }, [need.id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    setConfirmApproval(false);
    setConfirmDelete(false);
    creationAttempt.current = null;
    approvalKey.current = null;
    deletionKey.current = null;
    synchronizationKey.current = null;
  }, [need.id]);

  useEffect(() => {
    if (!['PENDING', 'IN_PROGRESS', 'DELETE_PENDING', 'RETRY_SCHEDULED'].includes(publication?.syncState)) {
      return undefined;
    }
    const timer = setInterval(() => load(true), 2000);
    return () => clearInterval(timer);
  }, [load, publication?.syncState]);

  useEffect(() => {
    const query = skillQuery.trim();
    if (query.length < 2) {
      setSkillSuggestions([]);
      setSkillSearchState('idle');
      return undefined;
    }
    let current = true;
    setSkillSearchState('loading');
    const timer = setTimeout(() => {
      jobConnectApi.searchSkills(query).then(response => {
        if (!current) return;
        setSkillSuggestions(response.items || []);
        setSkillSearchState('ready');
      }).catch(() => {
        if (!current) return;
        setSkillSuggestions([]);
        setSkillSearchState('error');
      });
    }, 250);
    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [skillQuery]);

  useEffect(() => {
    const query = locationQuery.trim();
    if (!['editing', 'ready'].includes(state) || query.length < 2 || selectedLocationQuery.current === query) {
      setLocationSuggestions([]);
      setLocationSearchState('idle');
      return undefined;
    }
    let current = true;
    setLocationSearchState('loading');
    const timer = setTimeout(() => {
      turnoverItApi.searchLocations(need.id, query).then(response => {
        if (!current) return;
        setLocationSuggestions(response.items || []);
        setLocationSearchState('ready');
      }).catch(() => {
        if (!current) return;
        setLocationSuggestions([]);
        setLocationSearchState('error');
      });
    }, 300);
    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [locationQuery, need.id, state]);

  const startDraft = () => {
    const nextDraft = initialDraft(need);
    selectedLocationQuery.current = '';
    setDraft(nextDraft);
    setLocationQuery(nextDraft.location.region);
    setSkillQuery(initialSkillQuery(need));
    setState('editing');
  };

  const selectLocation = suggestion => {
    const parts = suggestion.label.split(',').map(part => part.trim()).filter(Boolean);
    const locality = parts.length > 1 ? parts.shift() : undefined;
    const region = parts.join(', ') || suggestion.label;
    selectedLocationQuery.current = suggestion.label;
    setLocationQuery(suggestion.label);
    setLocationSuggestions([]);
    setLocationSearchState('idle');
    setDraft(current => ({
      ...current,
      location: {
        country: suggestion.countryCode === 'FR' ? 'France' : current.location.country,
        region,
        ...(locality ? { locality } : {}),
      },
    }));
    setErrors(current => ({ ...current, location: undefined }));
  };

  const editPublication = () => {
    const location = displayLocation(draft.location);
    selectedLocationQuery.current = location;
    setLocationQuery(location);
    setSkillQuery('');
    setErrors({});
    setMessage('');
    setState('editing');
  };

  const cancelEditing = () => {
    const savedDraft = publication.draftPayload;
    const location = displayLocation(savedDraft.location);
    selectedLocationQuery.current = location;
    setDraft(savedDraft);
    setLocationQuery(location);
    setSkillQuery('');
    setErrors({});
    setMessage('');
    setState('ready');
  };

  const update = (field, value) => {
    setDraft(current => ({ ...current, [field]: value }));
    setErrors(current => ({ ...current, [field]: undefined }));
  };

  const updateStatus = status => {
    setDraft(current => ({ ...current, status }));
  };

  const save = async () => {
    const nextErrors = validate(draft);
    setErrors(nextErrors);
    setMessage('');
    if (Object.keys(nextErrors).length) return;
    setSaving(true);
    try {
      let saved;
      if (publication) {
        saved = await jobConnectApi.replacePublication(need.id, publication.id, {
          expectedVersion: publication.version, draftPayload: draft, desiredStatus: draft.status,
        });
      } else {
        const fingerprint = JSON.stringify(draft);
        if (creationAttempt.current?.fingerprint !== fingerprint) {
          creationAttempt.current = {
            fingerprint,
            key: createJobConnectIdempotencyKey(),
          };
        }
        const creationDraft = {
          ...draft,
          reference: `offre-${need.id}-${creationAttempt.current.key}`,
        };
        saved = await jobConnectApi.createPublication(
          need.id,
          { draftPayload: creationDraft, desiredStatus: 'DRAFT' },
          creationAttempt.current.key,
        );
        creationAttempt.current = null;
      }
      setPublication(saved);
      setDraft(saved.draftPayload || draft);
      setState('ready');
      setMessage('Brouillon public enregistré.');
    } catch (error) {
      const fieldErrors = error?.response?.data?.error?.details?.fields;
      if (fieldErrors && Object.keys(fieldErrors).length) {
        setErrors(current => ({ ...current, ...fieldErrors }));
      } else {
        setMessage('Le brouillon n’a pas pu être enregistré. Vos informations sont conservées.');
      }
    } finally {
      setSaving(false);
    }
  };

  const approve = async () => {
    if (!publication) return;
    const fingerprint = JSON.stringify({ version: publication.version, decision: 'APPROVED' });
    if (approvalKey.current?.fingerprint !== fingerprint) {
      approvalKey.current = { fingerprint, key: createJobConnectIdempotencyKey() };
    }
    setSaving(true);
    try {
      await jobConnectApi.approvePublication(need.id, publication.id, {
        expectedVersion: publication.version,
        decision: 'APPROVED',
      }, approvalKey.current.key);
      approvalKey.current = null;
      setConfirmApproval(false);
      await load();
      setMessage('Offre validée.');
    } catch (error) {
      setMessage('La validation n’a pas abouti. Rechargez l’offre avant de continuer.');
    } finally {
      setSaving(false);
    }
  };

  const synchronize = async () => {
    if (!publication?.approvalWorkflowRequestId) return;
    const fingerprint = JSON.stringify({
      version: publication.version,
      desiredStatus: publication.desiredStatus,
      approvalWorkflowRequestId: publication.approvalWorkflowRequestId,
    });
    if (synchronizationKey.current?.fingerprint !== fingerprint) {
      synchronizationKey.current = { fingerprint, key: createJobConnectIdempotencyKey() };
    }
    setSaving(true);
    setMessage('');
    try {
      await jobConnectApi.synchronizePublication(need.id, publication.id, {
        expectedVersion: publication.version,
        desiredStatus: publication.desiredStatus,
      }, synchronizationKey.current.key);
      synchronizationKey.current = null;
      await load();
      setMessage('Envoi en cours.');
    } catch (error) {
      setMessage("L'envoi n'a pas abouti. Le brouillon est conservé.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!publication) return;
    const fingerprint = JSON.stringify({ version: publication.version });
    if (deletionKey.current?.fingerprint !== fingerprint) {
      deletionKey.current = { fingerprint, key: createJobConnectIdempotencyKey() };
    }
    setSaving(true);
    setMessage('');
    try {
      await jobConnectApi.deletePublication(
        need.id, publication.id, publication.version, deletionKey.current.key,
      );
      deletionKey.current = null;
      setConfirmDelete(false);
      await load();
      setMessage('Suppression en cours.');
    } catch (error) {
      setMessage("La suppression n'a pas abouti. L'offre reste suivie.");
    } finally {
      setSaving(false);
    }
  };

  if (state === 'loading') return <div aria-busy="true" className="flex min-h-[180px] items-center justify-center gap-2 text-gray-600"><Loader2 className="h-5 w-5 animate-spin" />Chargement de la diffusion…</div>;
  if (state === 'error') return <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-5 text-red-800"><p className="font-semibold">Impossible de charger la diffusion</p><p className="mt-1 text-sm">Réessayez dans quelques instants.</p><button className={`${buttonClass} mt-3 border border-red-300 bg-white`} onClick={load}>Réessayer</button></div>;
  if (state === 'empty') return <div className="rounded-lg border border-dashed border-gray-300 bg-white p-8 text-center"><h3 className="text-lg font-semibold">Aucune diffusion JobConnect préparée</h3><p className="mt-2 text-sm text-gray-600">Préparez une version publique sans information client sensible.</p>{canMutate && <button className={`${buttonClass} mt-4 bg-blue-600 text-white hover:bg-blue-700`} onClick={startDraft}>Créer un brouillon public</button>}</div>;

  const isTest = !publication || publication.credentialMode === 'TEST';
  const isSending = ['PENDING', 'IN_PROGRESS', 'DELETE_PENDING'].includes(publication?.syncState);
  const retryScheduled = publication?.syncState === 'RETRY_SCHEDULED';
  const failed = publication?.syncState === 'FAILED';
  const approved = Boolean(publication?.approvalWorkflowRequestId);
  const editing = state === 'editing';
  const statusLabel = isSending
    ? 'Envoi en cours'
    : failed
      ? "Échec de l'envoi"
      : publication?.effectiveStatus === 'PUBLISHED'
        ? 'Publiée'
        : publication?.effectiveStatus === 'INACTIVE'
          ? 'Inactive'
          : 'Brouillon prêt';
  const synchronizationLabel = publication?.desiredStatus === 'INACTIVE'
    ? 'Désactiver'
    : isTest
      ? 'Envoyer en brouillon de test'
      : publication?.desiredStatus === 'PUBLISHED'
        ? 'Publier'
        : 'Synchroniser le brouillon';

  return (
    <section aria-labelledby="jobconnect-title" className="space-y-4">
      <p role="note" className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">Avant diffusion, vérifiez que le contenu public ne contient pas d’informations confidentielles sur le client ou le projet. Cet avertissement ne bloque pas l’enregistrement ni la diffusion.</p>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h3 id="jobconnect-title" className="text-lg font-semibold text-gray-900">Diffusion sur JobConnect</h3><p className="text-sm text-gray-600">Version publique de l’offre</p></div>
        {isTest ? <span className="rounded-full bg-gray-900 px-3 py-1.5 text-xs font-bold text-white">TEST — non publié</span> : publication?.effectiveStatus === 'PUBLISHED' && <span className="rounded-full bg-green-100 px-3 py-1.5 text-xs font-bold text-green-800">Publié</span>}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="rounded-lg border border-blue-100 bg-blue-50 p-4"><Briefcase className="mb-3 h-5 w-5 text-blue-700" /><span className="block text-xs font-semibold uppercase tracking-wide text-gray-600">Postes à pourvoir</span><strong className="mt-1 block text-2xl text-gray-900">{need.quantity || 1}</strong></div>
        <div className="rounded-lg border border-blue-100 bg-blue-50 p-4"><Users className="mb-3 h-5 w-5 text-blue-700" /><span className="block text-xs font-semibold uppercase tracking-wide text-gray-600">Candidatures reçues</span><strong className="mt-1 block text-2xl text-gray-900">{applicationCount}</strong></div>
        <div className="rounded-lg border border-blue-100 bg-blue-50 p-4"><CheckCircle className="mb-3 h-5 w-5 text-blue-700" /><span className="block text-xs font-semibold uppercase tracking-wide text-gray-600">État de diffusion</span><strong className="mt-1 block text-base text-gray-900">{statusLabel}</strong>{isSending && <span className="mt-1 block text-xs text-gray-600">Attendre la fin de l’envoi</span>}</div>
      </div>

      {retryScheduled && <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">Nouvel essai automatique prévu {publication.nextRetryAt ? new Date(publication.nextRetryAt).toLocaleString('fr-FR') : 'prochainement'}.</p>}
      {failed && <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">L'envoi n'a pas abouti. Le brouillon est conservé.</p>}

      {publication && !editing && <article className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <header className="border-b border-gray-100 bg-gray-50 px-5 py-5 sm:px-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-blue-700">Offre JobConnect</p>
          <h4 className="mt-1 text-xl font-semibold text-gray-900">{draft.title}</h4>
          <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-sm text-gray-600">
            <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4" />{displayLocation(draft.location)}</span>
            <span>{contractLabels[draft.contract?.[0]] || draft.contract?.[0]}</span>
            <span>{remoteLabels[draft.remote] || draft.remote}</span>
          </div>
        </header>
        <div className="space-y-5 px-5 py-5 sm:px-6">
          <div><h5 className="text-sm font-semibold text-gray-900">Description</h5><p className="mt-2 whitespace-pre-line text-sm leading-6 text-gray-700">{draft.description}</p></div>
          <div><h5 className="text-sm font-semibold text-gray-900">Compétences</h5><div className="mt-2 flex flex-wrap gap-2">{draft.skills.map(skill => <span key={skill} className="rounded-full bg-blue-50 px-3 py-1 text-sm font-medium text-blue-800">{skill}</span>)}</div></div>
        </div>
      </article>}

      {editing && <details className="rounded-lg border border-gray-200 bg-gray-50 p-4" open={sourceOpen} onToggle={event => setSourceOpen(event.currentTarget.open)}>
        <summary className="flex min-h-[44px] cursor-pointer items-center justify-between font-semibold focus:outline-none focus:ring-2 focus:ring-blue-600">Source interne — lecture seule <ChevronDown className="h-4 w-4" /></summary>
        <dl className="mt-3 grid gap-2 text-sm md:grid-cols-2"><div><dt className="font-medium text-gray-600">Besoin</dt><dd>{need.title}</dd></div><div><dt className="font-medium text-gray-600">Lieu interne</dt><dd>{need.jdLocation || 'Non renseigné'}</dd></div><div className="md:col-span-2"><dt className="font-medium text-gray-600">Contexte privé</dt><dd>{need.description || 'Non renseigné'}</dd></div></dl>
        <p className="mt-3 text-xs text-gray-500">Seul le contenu de la fiche publique est envoyé. Relisez les informations reprises de la source interne avant diffusion.</p>
      </details>}

      {editing && <div className="rounded-lg border border-gray-200 bg-white p-4">
        <h4 className="font-semibold">Brouillon public</h4>
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          <label className="text-sm font-medium">Titre public<input className={fieldClass} maxLength="100" value={draft.title} onChange={event => update('title', event.target.value)} aria-invalid={Boolean(errors.title)} aria-describedby={errors.title ? 'title-error' : undefined} />{errors.title && <span id="title-error" className="mt-1 block text-sm text-red-700">{errors.title}</span>}</label>
          <label className="text-sm font-medium">Contrat<select className={fieldClass} value={draft.contract[0] || ''} onChange={event => update('contract', event.target.value ? [event.target.value] : [])} aria-invalid={Boolean(errors.contract)} aria-describedby={errors.contract ? 'contract-error' : undefined}><option value="">Choisir</option><option value="PERMANENT">CDI</option><option value="TEMPORARY">CDD</option><option value="FREELANCE">Freelance</option><option value="INTERNSHIP">Stage</option><option value="APPRENTICESHIP">Alternance</option></select>{errors.contract && <span id="contract-error" className="mt-1 block text-sm text-red-700">{errors.contract}</span>}</label>
          <label className="text-sm font-medium md:col-span-2">Description publique<textarea className={`${fieldClass} min-h-[160px]`} maxLength="8000" value={draft.description} onChange={event => update('description', event.target.value)} aria-invalid={Boolean(errors.description)} aria-describedby={errors.description ? 'description-error' : undefined} />{errors.description && <span id="description-error" className="mt-1 block text-sm text-red-700">{errors.description}</span>}</label>
          <div className="text-sm font-medium">
            <label htmlFor="jobconnect-location-query">Lieu public</label>
            <input id="jobconnect-location-query" role="combobox" className={fieldClass} maxLength="150" value={locationQuery} onChange={event => { selectedLocationQuery.current = ''; setLocationQuery(event.target.value); setDraft(current => ({ ...current, location: { country: current.location.country, region: event.target.value } })); setErrors(current => ({ ...current, location: undefined })); }} aria-expanded={locationSuggestions.length > 0} aria-controls="jobconnect-location-suggestions" aria-autocomplete="list" aria-invalid={Boolean(errors.location)} aria-describedby={errors.location ? 'location-help location-error' : 'location-help'} autoComplete="off" />
            <span id="location-help" className="mt-1 block text-xs text-gray-500">Sélectionnez une localisation proposée pour renseigner la ville et la région.</span>
            {locationSearchState === 'loading' && <span className="mt-1 block text-xs text-gray-500">Recherche des localisations…</span>}
            {locationSearchState === 'error' && <span className="mt-1 block text-xs text-red-700">Les suggestions de localisation sont indisponibles.</span>}
            {locationSuggestions.length > 0 && <div id="jobconnect-location-suggestions" role="listbox" className="mt-2 flex flex-col gap-2">{locationSuggestions.map(location => <button key={location.value} type="button" role="option" aria-selected="false" aria-label={`Choisir la localisation ${location.shortLabel || location.label}`} onClick={() => selectLocation(location)} className="min-h-[44px] rounded-md border border-blue-300 bg-blue-50 px-3 py-2 text-left text-blue-800">{location.label}</button>)}</div>}
            {errors.location && <span id="location-error" className="mt-1 block text-sm text-red-700">{errors.location}</span>}
          </div>
          <label className="text-sm font-medium">Télétravail<select className={fieldClass} value={draft.remote} onChange={event => update('remote', event.target.value)}><option value="NONE">Sur site</option><option value="PARTIAL">Partiel</option><option value="FULL">Complet</option></select></label>
          <label className="text-sm font-medium">Statut souhaité<select className={fieldClass} value={draft.status} onChange={event => updateStatus(event.target.value)}><option value="DRAFT">Brouillon</option><option value="PUBLISHED">Publier</option><option value="INACTIVE">Désactiver</option></select></label>
          <div className="text-sm font-medium md:col-span-2">
            <label htmlFor="jobconnect-skill-query">Compétence JobConnect</label>
            <input id="jobconnect-skill-query" className={fieldClass} value={skillQuery} onChange={event => setSkillQuery(event.target.value)} aria-invalid={Boolean(errors.skills)} aria-describedby={errors.skills ? 'skills-help skills-error' : 'skills-help'} autoComplete="off" />
            <span id="skills-help" className="mt-1 block text-xs text-gray-500">Sélectionnez uniquement une compétence proposée par JobConnect.</span>
            {skillSearchState === 'loading' && <span className="mt-1 block text-xs text-gray-500">Recherche des compétences…</span>}
            {skillSearchState === 'error' && <span className="mt-1 block text-xs text-red-700">Les suggestions JobConnect sont indisponibles.</span>}
            {skillSuggestions.length > 0 && <div className="mt-2 flex flex-wrap gap-2">{skillSuggestions.map(skill => <button key={skill.slug} type="button" aria-label={`Ajouter la compétence ${skill.name}`} onClick={() => { setDraft(current => ({ ...current, skills: [...new Set([...current.skills, skill.slug])] })); setErrors(current => ({ ...current, skills: undefined })); setSkillQuery(''); setSkillSuggestions([]); }} className="min-h-[36px] rounded-md border border-blue-300 bg-blue-50 px-3 py-1 text-blue-800">{skill.name}</button>)}</div>}
            {draft.skills.length > 0 && <div className="mt-2 flex flex-wrap gap-2">{draft.skills.map(skill => <button key={skill} type="button" aria-label={`Retirer la compétence ${skill}`} onClick={() => setDraft(current => ({ ...current, skills: current.skills.filter(value => value !== skill) }))} className="min-h-[36px] rounded-full bg-gray-100 px-3 py-1 text-gray-800">{skill} ×</button>)}</div>}
            {errors.skills && <span id="skills-error" className="mt-1 block text-sm text-red-700">{errors.skills}</span>}
          </div>
        </div>
      </div>}

      {editing && <div className="rounded-lg border border-gray-200 bg-white p-4"><h4 className="font-semibold">Validation</h4>{Object.keys(validate(draft)).length ? <p className="mt-2 flex items-center gap-2 text-sm text-amber-800"><AlertTriangle className="h-4 w-4" />Des informations publiques restent à corriger.</p> : <p className="mt-2 flex items-center gap-2 text-sm text-green-800"><CheckCircle className="h-4 w-4" />Les champs publics obligatoires sont prêts.</p>}</div>}
      {editing && publication && <div className="rounded-lg border border-gray-200 bg-white p-4"><h4 className="font-semibold">Changements à vérifier</h4>{need.title !== draft.title ? <p className="mt-2 text-sm"><span className="sr-only">Ancien titre supprimé : </span><del className="text-gray-600">{need.title}</del><span className="sr-only">. Nouveau titre ajouté : </span><ins className="ml-2 font-medium no-underline">{draft.title}</ins></p> : <p className="mt-2 text-sm text-gray-600">Aucun changement de titre. Relisez le contenu public avant de le valider.</p>}<p className="mt-2 text-sm text-gray-600">Toute modification future demandera une nouvelle validation.</p></div>}

      {message && <p role="alert" className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">{message}</p>}
      {confirmApproval && <div className="rounded-lg border border-amber-200 bg-amber-50 p-4"><p className="font-semibold">Valider cette version publique ?</p><p className="mt-1 text-sm">Toute modification future demandera une nouvelle validation.</p><div className="mt-3 flex gap-2"><button className={`${buttonClass} border border-gray-300 bg-white`} disabled={saving} onClick={() => setConfirmApproval(false)}>Annuler</button><button className={`${buttonClass} bg-blue-600 text-white`} disabled={saving} onClick={approve}>Confirmer la validation</button></div></div>}
      {confirmDelete && <div className="rounded-lg border border-red-200 bg-red-50 p-4"><p className="font-semibold text-red-800">Supprimer l'offre JobConnect ?</p><p className="mt-1 text-sm text-red-800">Cette action demande aussi sa suppression distante et ne peut pas être annulée.</p><div className="mt-3 flex gap-2"><button className={`${buttonClass} border border-gray-300 bg-white`} onClick={() => setConfirmDelete(false)}>Annuler</button><button className={`${buttonClass} border border-red-300 bg-white text-red-700`} disabled={saving} onClick={remove}>Supprimer l'offre JobConnect</button></div></div>}
      {canMutate && editing && <div className="flex flex-wrap gap-2"><button className={`${buttonClass} bg-blue-600 text-white hover:bg-blue-700`} disabled={saving} onClick={save}>{saving ? 'Enregistrement…' : 'Enregistrer'}</button>{publication && <button className={`${buttonClass} border border-gray-300 bg-white text-gray-700`} disabled={saving} onClick={cancelEditing}>Annuler les modifications</button>}</div>}
      {canMutate && publication && !editing && <div className="rounded-lg border border-gray-200 bg-white p-4"><h4 className="font-semibold text-gray-900">Gérer l'offre</h4><div className="mt-3 flex flex-wrap gap-2"><button className={`${buttonClass} inline-flex items-center gap-2 border border-gray-300 bg-white text-gray-800`} disabled={saving || isSending} onClick={editPublication}><Pencil className="h-4 w-4" />Modifier l'offre</button>{!approved && <button className={`${buttonClass} border border-blue-300 bg-white text-blue-700`} disabled={saving || isSending || Object.keys(validate(draft)).length > 0} onClick={() => setConfirmApproval(true)}>Valider cette offre</button>}<button className={`${buttonClass} bg-gray-900 text-white`} disabled={!approved || isSending || saving} onClick={synchronize}>{synchronizationLabel}</button>{onOpenApplications && <button className={`${buttonClass} border border-gray-300 bg-white`} onClick={onOpenApplications}>Voir les candidatures reçues</button>}<button className={`${buttonClass} border border-red-300 bg-white text-red-700`} disabled={saving || isSending} onClick={() => setConfirmDelete(true)}>Supprimer</button></div></div>}
    </section>
  );
};

export default JobConnectDiffusionPanel;
